import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
  Optional,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {DateTime} from 'luxon';
import {filter} from 'rxjs';
import {IDropdownObject} from 'src/app/interfaces/dropdown-object.interface';
import {
  ILongTermLeaveDialog,
  ILongTermLeaveDropdowns,
  LongTermLeaveType,
} from 'src/app/interfaces/long-term-leave.interface';
import {DIALOG_DATA} from 'src/app/services/dialog.service';
import {LongTermLeaveService} from 'src/app/services/long-term-leave/long-term-leave.service';
import {DialogRef} from 'src/app/shared/ui-kits/dialog/dialog-ref';
import {Utils} from 'src/app/shared/utils/utils';
import {wordCountValidator} from 'src/app/validators/custom.validators';

@UntilDestroy()
@Component({
  selector: 'app-long-term-leave-popup',
  templateUrl: './long-term-leave-popup.component.html',
  styleUrls: ['./long-term-leave-popup.component.scss'],
})
export class LongTermLeavePopupComponent implements OnInit {
  public longTermLeaveForm!: UntypedFormGroup;
  public isoStartDate!: Date;
  public isDatePickerOpened!: boolean;

  public allDropdowns!: ILongTermLeaveDropdowns;
  public phoneMask: string = '+#00000000000';
  phonePatterns = {
    '#': {pattern: new RegExp('[1-9]')},
    '0': {pattern: new RegExp('[0-9]')},
  };

  get mobilizationForm(): FormGroup {
    return this.formControl('mobilization')! as FormGroup;
  }

  get contactPerson(): FormGroup {
    return this.mobilizationFormControl('emergencyContactPerson')! as FormGroup;
  }

  get startDate(): AbstractControl {
    return this.longTermLeaveForm.get('startDate')!;
  }

  get type(): AbstractControl {
    return this.longTermLeaveForm.get('longTermLeaveType')!;
  }

  get corporateAccount(): AbstractControl {
    return this.mobilizationForm.get('corporateAccount')!;
  }

  get isAccountBlockDate(): boolean {
    return (
      this.corporateAccount?.getRawValue()?.name === 'Specific day and time'
    );
  }

  get isMobilization(): boolean {
    return (
      this.longTermLeaveForm.get('longTermLeaveType')!.getRawValue()?.id ===
      LongTermLeaveType.Mobilization
    );
  }

  get isCreate(): boolean {
    return !Boolean(this.data.id);
  }

  get corporateAccountOptionsList(): IDropdownObject[] {
    return this.allDropdowns.AccountBlockOption ?? [];
  }

  get equipmentOptionsList(): IDropdownObject[] {
    return this.allDropdowns.EquipmentOption ?? [];
  }

  get accountCorrespondenceOptionList(): IDropdownObject[] {
    return this.allDropdowns.AccountCorrespondenceOption ?? [];
  }

  get relationships(): IDropdownObject[] {
    return this.allDropdowns.Relationship ?? [];
  }

  get longTermLeaveTypes(): IDropdownObject[] {
    return this.allDropdowns?.Type ?? [];
  }

  isMobilizationFieldsDisabled: boolean = false;

  private readonly MOBILIZATION_TYPE = 'Mobilization';
  private readonly RELATIONSHIP_OTHER_TYPE = 'Other';

  constructor(
    private _fb: UntypedFormBuilder,
    private dialogRef: DialogRef,
    @Optional() @Inject(DIALOG_DATA) public data: ILongTermLeaveDialog,
    private longTermLeave: LongTermLeaveService,
    private cdr: ChangeDetectorRef
  ) {
    if (this.data.type === LongTermLeaveType.Mobilization) {
      this.setMobilizationDropdownsAvailability(data.startDate);
    }
  }

  ngOnInit(): void {
    this.getDropdowns();
    this.buildLongTermLeaveForm();

    this.subscriptionToTypeChanges();
    this.subscriptionToCorporateAccountChanges();
  }

  getDropdowns() {
    this.longTermLeave
      .getDropdowns()
      .pipe(untilDestroyed(this))
      .subscribe(dropdowns => {
        this.allDropdowns = dropdowns;
        this.updateFormFields();
      });
  }

  updateFormFields() {
    this.type.patchValue(
      this.longTermLeaveTypes.find(
        el => el.id === (this.data.type ?? LongTermLeaveType.Sabbatical)
      )
    );
    if (this.isMobilization) {
      this.mobilizationForm.patchValue({
        ...this.data,
        createdBy: this.data.createdByOptionList.find(
          el => el.id === this.data.createdBy.id
        ),
      });

      this.updateContactPerson();
    }
  }

  updateContactPerson() {
    const emergencyContactPerson = this.data.emergencyContactPerson ?? null;
    this.contactPerson.patchValue({
      ...emergencyContactPerson,
      relationship: emergencyContactPerson
        ? (this.data.emergencyContactPerson?.relationship ??
          this.allDropdowns.Relationship.find(
            el => el.name === this.RELATIONSHIP_OTHER_TYPE
          ))
        : null,
    });
  }

  buildLongTermLeaveForm() {
    this.longTermLeaveForm = this._fb.group({
      id: new UntypedFormControl(this.data.id),
      employeeId: new UntypedFormControl(this.data.employeeId),
      longTermLeaveType: new FormControl<IDropdownObject | null>(null, [
        Validators.required,
      ]),
      startDate: new FormControl<string | null>(this.data.startDate, [
        Validators.required,
      ]),
      endDate: new FormControl<string | null>(this.data.endDate!),
      comment: new FormControl<string | null>(this.data.comment, [
        wordCountValidator(400),
      ]),
      mobilization: new FormGroup({
        createdBy: new FormControl<IDropdownObject | null>(null),
        corporateAccount: new FormControl<IDropdownObject | null>(null),
        accountCorrespondence: new FormControl<IDropdownObject | null>(null),
        equipment: new FormControl<IDropdownObject | null>(null),
        accountBlockDate: new FormControl<string | null>(null),
        emergencyContactPerson: new FormGroup({
          name: new FormControl<string>(
            {
              value: '',
              disabled: this.isMobilizationFieldsDisabled,
            },
            [Validators.maxLength(40)]
          ),
          phoneNumber: new FormControl<string | null>({
            value: null,
            disabled: this.isMobilizationFieldsDisabled,
          }),
          relationship: new FormControl<IDropdownObject | null>(null),
          id: new FormControl<number | null>(this.data.id),
          employeeId: new FormControl<number | null>(this.data.employeeId),
        }),
      }),
    });

    this.longTermLeaveForm.setValidators(this.dateValidator());
  }

  subscriptionToTypeChanges() {
    this.type.valueChanges
      .pipe(
        untilDestroyed(this),
        filter(data => data)
      )
      .subscribe((value: IDropdownObject) => {
        if (value.name === this.MOBILIZATION_TYPE) {
          this.setMobilizationValidators();
        } else this.removeMobilizationValidators();

        this.cdr.detectChanges();
      });
  }

  subscriptionToCorporateAccountChanges() {
    this.corporateAccount!.valueChanges.pipe(
      untilDestroyed(this),
      filter(account => account)
    ).subscribe(() => {
      this.setAccountBlockDateValidator();
    });
  }

  setMobilizationValidators() {
    this.setValidators('createdBy', Validators.required, this.mobilizationForm);
    this.setValidators(
      'corporateAccount',
      Validators.required,
      this.mobilizationForm
    );
    this.setValidators('equipment', Validators.required, this.mobilizationForm);
    this.setValidators(
      'accountCorrespondence',
      Validators.required,
      this.mobilizationForm
    );
    this.setValidators('name', Validators.required, this.contactPerson);
    this.setValidators('relationship', Validators.required, this.contactPerson);
    this.setValidators('phoneNumber', Validators.required, this.contactPerson);

    this.updateContactPerson();
    this.mobilizationForm.enable();
  }

  removeMobilizationValidators() {
    this.removeValidators('createdBy', this.mobilizationForm);
    this.removeValidators('corporateAccount', this.mobilizationForm);
    this.removeValidators('equipment', this.mobilizationForm);
    this.removeValidators('accountCorrespondence', this.mobilizationForm);
    this.removeValidators('name', this.contactPerson);
    this.removeValidators('relationship', this.contactPerson);
    this.removeValidators('phoneNumber', this.contactPerson);

    this.resetMobilizationForm();
    this.mobilizationForm.disable();
  }

  resetMobilizationForm() {
    this.mobilizationForm.reset();
  }

  setAccountBlockDateValidator() {
    if (this.isAccountBlockDate) {
      this.setValidators(
        'accountBlockDate',
        Validators.required,
        this.mobilizationForm
      );
    } else {
      this.removeValidators('accountBlockDate', this.mobilizationForm);
    }
  }

  setValidators(
    controlName: string,
    validators: ValidatorFn | ValidatorFn[] | null,
    form: FormGroup = this.longTermLeaveForm
  ) {
    form.get(controlName)?.setValidators(validators);
  }

  removeValidators(
    controlName: string,
    form: FormGroup = this.longTermLeaveForm
  ) {
    form.get(controlName)?.removeValidators([Validators.required]);
    form.get(controlName)?.updateValueAndValidity();
  }

  formControl(control: string): AbstractControl {
    return this.longTermLeaveForm.get(control)!;
  }

  mobilizationFormControl(control: string): AbstractControl {
    return this.mobilizationForm.get(control)!;
  }

  save() {
    if (this.longTermLeaveForm.invalid) {
      Utils.validateAllFormFields(this.longTermLeaveForm);
      return;
    }
    this.type.disable();

    const result = {
      longTermLeave: this.longTermLeaveForm.getRawValue(),
      type: this.type.getRawValue(),
    };

    this.dialogRef.close(result);
  }

  cancel() {
    this.dialogRef.close();
  }

  dateValidator(): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      const form = control;

      const startDateValue = form.get('startDate')?.value;
      const endDateValue = form.get('endDate')?.value;

      const startDate: Date | null = startDateValue
        ? Utils.formatStringToDate(startDateValue)
        : null;

      const endDate: Date | null = endDateValue
        ? Utils.formatStringToDate(endDateValue)
        : null;

      if (!startDate || !endDate) {
        return null;
      }

      return endDate < startDate ? {invalidDate: true} : null;
    };
  }

  private setMobilizationDropdownsAvailability(startDate: string) {
    const shouldBeDisabled = startDate
      ? Utils.formatStringToDateTime(startDate).startOf('day') <=
        DateTime.now().startOf('day')
      : false;

    this.isMobilizationFieldsDisabled = shouldBeDisabled;
  }
}
